import { Injectable, Injector, OnDestroy } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { UserManager, Log, UserManagerSettings, User } from 'oidc-client';
import { AraxUser } from '../../models/auth/arax-user';
import { BaMenuService } from 'app/shared/services/baMenu/baMenu.service';
import { Observable, Subject } from 'rxjs';
import { BusService } from 'app/shared/services/eventbus/bus.service';
import { Message } from 'app/shared/services/eventbus/message';
import { environment } from 'environments/environment';
import { AuthorizeService, PasswordPolicyService } from '../control';
import { Notification } from 'app/shared/models/hubs/enums/notification';
import { PatientRelease } from 'app/shared/models/hubs/enums/patient-release';
import { SignalrService } from '../notificationService';
import { QMSTicket } from 'app/shared/models/hubs';
import { InactivityService } from '../inactivity/inactivity.service';

@Injectable()
export class AuthService implements OnDestroy {

    private readonly mgr: UserManager;

    private oidcUser: User;

    user: AraxUser = null;
    authHeaders: HttpHeaders;

    // An observable to fire when a user object changes
    // usually after signin/signout
    userChanged: Observable<AraxUser>;
    isLoadData: boolean;
    permission: string[] = [];

    private readonly userSubject: Subject<AraxUser> = new Subject<AraxUser>();

    readonly settings: UserManagerSettings = {
        authority: environment.authorityService,
        client_id: 'js',
        redirect_uri: environment.redirectURI,
        post_logout_redirect_uri: environment.postLogoutRedirectURI,
        response_type: 'id_token token',
        scope: 'openid profile api',

        // silent renew will get a new access_token via an iframe 
        // just prior to the old access_token expiring (60 seconds prior)
        silent_redirect_uri: environment.silentRedirectURI,
        automaticSilentRenew: true,
        // silentRequestTimeout:10000,

        // will revoke (reference) access tokens at logout time
        revokeAccessTokenOnSignout: true,
        filterProtocolClaims: false,

        // this will toggle if profile endpoint is used
        loadUserInfo: true,

        // clockSkew: 36000,
    };

    constructor(
        private readonly menuService: BaMenuService,
        private readonly busService: BusService,
        private readonly _authorizationService: AuthorizeService,
        private readonly _signalrService: SignalrService,
        private readonly injector: Injector) {
        // Attach logger only on dev
        Log.logger = console;
        Log.level = Log.INFO;

        this.mgr = new UserManager(this.settings);
        this.userChanged = this.userSubject.asObservable();
        const instance = this;

        this.mgr.events.addAccessTokenExpiring(() => {
            // Access Token Expiring
            Log.info('addAccessTokenExpiring called');
        });

        this.mgr.events.addUserUnloaded(() => {
            Log.info('addUserUnloaded called');
            // User logged out locally
            // User must be redirected to login
            // instance.mgr.signinRedirect(this.currentPageState);
        });

        this.mgr.events.addUserLoaded(user => {
            // User logged in successfully
            Log.info('addUserLoaded called', user);
            instance.setUser(user);
        });

        this.mgr.events.addUserSignedOut(() => {
            Log.info('addUserSignedOut called');
            // User logged out from security
            // User must be redirected to login
            // instance.mgr.signinRedirect(this.currentPageState);
        });

        this.mgr.events.addSilentRenewError(e => {
            Log.error('silent renew error', e.message);
        });

        this.userChanged = this.userSubject.asObservable();
    }

    ngOnDestroy(): void {
        if (this.userSubject) {
            this.userSubject.complete();
        }
    }

    get currentPageState() {
        const currentPath = globalThis.location.pathname || '/';
        const normalizedPath = currentPath.toLowerCase();

        if (normalizedPath.startsWith('/callback') || normalizedPath.startsWith('/silent')) {
            return { state: '/' };
        }

        return { state: currentPath };
    }

    logout() {
        this._authorizationService.clearPermissions()
        this.mgr.signoutRedirect();
    }

    signinSilentCallback() {
        this.mgr.signinSilentCallback();
    }

    signinSilent = (): Promise<boolean> => {
        return this.mgr.signinSilent(this.currentPageState).then((user) => {
            Log.info('signinSilent called, user object is:', user);

            if (user) {
                this.setUser(user);
                return true;
            }

            this.signinRedirect();
            return false;
        }).catch((e) => {
            Log.error('signinSilent exception', e);
            this.signinRedirect();
            return false;
        });
    }

    signinRedirect() {
        this.mgr.createSigninRequest(this.currentPageState).then((request) => {
            globalThis.location.replace(request.url);
        }).catch((e) => {
            Log.error('createSigninRequest failed, fallback to signinRedirect', e);
            this.mgr.signinRedirect(this.currentPageState);
        });
    }

    async setPermission() {
        this.permission = await this._authorizationService.getUserPermission();
        this._authorizationService.permissions = this.permission;
        this.menuService.buildMenu(this._authorizationService);
    }

    async setUser(user?: User) {
        if (user) {
            this.oidcUser = user;
            this.setAuthHeaders(user);
            this.user = new AraxUser(user);
            await this.setPermission();
            this.connectNotification();
            this.loadPolicyAndStart();

        } else {
            this.oidcUser = null;
            this.user = null;
            this.authHeaders = new HttpHeaders();
        }
        // send new user object to subscribers,like the one in BaseComponent
        this.userSubject.next(this.user);
        // since we're not streaming objects,it's a good practice to call complete
        this.userSubject.complete();
    }

   private loadPolicyAndStart(): void {
    const passwordPolicyService = this.injector.get(PasswordPolicyService);
    const inactivityService = this.injector.get(InactivityService);

    passwordPolicyService.get()
        .toPromise()
        .then((data: any) => {
            if (data?.ActivateSystemLogoutTime && data?.TotalSystemLogoutTime) {
                inactivityService.updateSessionTimeout(
                    data.TotalSystemLogoutTime * 60 * 1000
                );
            }
            inactivityService.startTracking();
        })
        .catch((error) => {
            Log.error('Failed to load password policy:', error);
            inactivityService.startTracking();
        });
}

    clearState() {
        this.mgr.clearStaleState().then(() => {
            Log.info('clearStateState success');
        }).catch(e => {
            Log.error('clearStateState error', e.message);
        });
    }

    getUser() {
        this.mgr.getUser().then((user: User) => {
            Log.info('got user', user);
            this.setUser(user);

        }).catch(err => {
            Log.error('got user exception', err);
        });
    }

    removeUser() {
        this.mgr.removeUser().then(() => {
            this.setUser();
            Log.info('user removed');
        }).catch(err => {
            Log.error('removeUser', err);
        });
    }

    startSigninMainWindow() {
        this.mgr.signinRedirect().then(() => {
            Log.info('signinRedirect done');
        }).catch(err => {
            Log.error('startSigninMainWindow', err);
        });
    }

    signinRedirectCallback = (): Promise<any> => {
        return new Promise((resolve) => {
            this.mgr.signinRedirectCallback().then((user: User) => {
                Log.info('signinRedirectCallback', user);
                globalThis.history.replaceState({},
                    globalThis.document.title,
                    globalThis.location.origin + globalThis.location.pathname);
                resolve(user.state);
            }).catch(err => {
                Log.error('signinRedirectCallback', err);
                resolve(null);
            });
        });
    }

    startSignoutMainWindow() {
        this.mgr.signoutRedirect().then(resp => {
            Log.info('signed out', resp);
        }).catch(err => {
            Log.error('signed out exception', err);
        });
    }

    endSignoutMainWindow() {
        this.mgr.signoutRedirectCallback().then(resp => {
            Log.info('signed out', resp);
            this.setUser();

        }).catch(err => {
            Log.error('signed out exception', err);
        });
    }

    private setAuthHeaders(user: User) {
        this.authHeaders = new HttpHeaders();
        this.authHeaders.append('Authorization', `${user.token_type} ${user.access_token}`);
        this.authHeaders.append('Content-Type', 'application/json');
        this.busService.publishMessage(new Message());
    }

    getTokenType() {
        if (this.oidcUser?.token_type) {
            return `${this.oidcUser?.token_type} ${this.oidcUser?.access_token}`;
        } else {
            return '';
        }
    }

    getAccessToken() {
        if (this.oidcUser?.token_type) {
            return `${this.oidcUser?.access_token}`;
        } else {
            return '';
        }
    }

    connectNotification() {
        if (!environment.enableNotification) {
            this.isLoadData = true;
            return;
        }
        const notificationConfig = {
            subcribeToUser: true,
            notifyWhenReceived: true,
            subcribeToSection: false,
            sectionId: null,
            notifyMessage: 'You have received a new notification',
        };
        this._signalrService.connect(Notification.Name, Notification.SubscribeToRoles, notificationConfig);
        this._signalrService.connect(PatientRelease.Name, PatientRelease.ReceiveReleaseResult);
        this._signalrService.connect(QMSTicket.Name, QMSTicket.SubscribeToQmsTiket, { subcribeToUser: true, isConnectToGroup: true, });
        this.isLoadData = true;
    }

    extendSession(): Promise<boolean> {
        return this.signinSilent();
    }
}

